import React from "react";
import { motion } from "framer-motion";

// MATERIAL UI COMPONENTS
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import Avatar from "@mui/material/Avatar";
import { alpha, useTheme } from "@mui/material/styles";

// ICONS
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import BlockIcon from "@mui/icons-material/Block";

// UTILS
import { phoneMask } from "../../utils/formatters";
import { getChannelLogoPath } from "../../utils/merchant";

const itemVariants = {
  hidden: { y: 10, opacity: 0 },
  show: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
};

export default function CustomerRow({ customer, deleteAction, editAction }) {
  const theme = useTheme();

  if (!customer) {
    return null;
  }

  const isBlacklisted = customer.is_blacklisted;
  const channelLogoPath = getChannelLogoPath(customer.channel);
  const hasChannelLogo = Boolean(channelLogoPath);

  const getIconBackgroundColor = () => {
    if (isBlacklisted) {
      return alpha(theme.palette.error.main, 0.1);
    }

    return alpha(theme.palette.primary.main, 0.1);
  };

  const getIconColor = () => {
    if (isBlacklisted) {
      return theme.palette.error.main;
    }

    return theme.palette.primary.main;
  };

  const getPaperBackgroundColor = () => {
    if (isBlacklisted) {
      return alpha(theme.palette.error.main, 0.03);
    }

    return theme.palette.grey[50];
  };

  const getBorderColor = () => {
    if (isBlacklisted) {
      return alpha(theme.palette.error.main, 0.3);
    }

    return alpha(theme.palette.grey[300], 0.5);
  };

  const getHoverBorderColor = () => {
    if (isBlacklisted) {
      return alpha(theme.palette.error.main, 0.5);
    }

    return theme.palette.grey[300];
  };

  const getNameColor = () => {
    if (isBlacklisted) {
      return theme.palette.text.secondary;
    }

    return theme.palette.text.primary;
  };

  const getNameTextDecoration = () => {
    if (isBlacklisted) {
      return "line-through";
    }

    return "none";
  };

  const renderChannelAvatar = () => {
    if (!hasChannelLogo) {
      return null;
    }

    return (
      <Avatar
        src={channelLogoPath}
        sx={{
          width: 20,
          height: 20,
          borderRadius: 100,
          ml: 1,
        }}
      />
    );
  };

  const renderCustomerIcon = () => {
    if (isBlacklisted) {
      return <BlockIcon fontSize="small" />;
    }

    return <PersonOutlineIcon fontSize="small" />;
  };

  const renderBlacklistedChip = () => {
    if (!isBlacklisted) {
      return null;
    }

    return (
      <Chip
        icon={<BlockIcon sx={{ fontSize: 14 }} />}
        label="Não Confiável"
        size="small"
        sx={{
          height: 20,
          bgcolor: theme.palette.error.main,
          color: theme.palette.error.contrastText,
          fontWeight: 600,
          fontSize: "0.75rem",
          "& .MuiChip-icon": {
            color: theme.palette.error.contrastText,
          },
        }}
      />
    );
  };

  const renderPhoneNumber = () => {
    if (!customer.phonenmbr) {
      return null;
    }

    return (
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <PhoneIcon
          fontSize="small"
          sx={{
            fontSize: 14,
            color: theme.palette.text.secondary,
          }}
        />
        <Typography variant="body2" color="text.secondary" fontWeight={400}>
          {phoneMask(customer.phonenmbr)}
        </Typography>
      </Stack>
    );
  };

  const renderEmail = () => {
    if (!customer.email) {
      return null;
    }

    return (
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <MailOutlineIcon
          fontSize="small"
          sx={{
            fontSize: 14,
            color: theme.palette.text.secondary,
          }}
        />
        <Typography variant="body2" color="text.secondary" fontWeight={400}>
          {customer.email}
        </Typography>
      </Stack>
    );
  };

  const renderAddress = () => {
    if (!customer.address) {
      return null;
    }

    return (
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <LocationOnOutlinedIcon
          fontSize="small"
          sx={{
            fontSize: 14,
            color: theme.palette.text.secondary,
          }}
        />
        <Typography
          variant="body2"
          color="text.secondary"
          fontWeight={400}
          noWrap
          sx={{ maxWidth: { xs: "100%", sm: 180, md: 250 } }}
        >
          {customer.address}
        </Typography>
      </Stack>
    );
  };

  return (
    <Box sx={{ mb: 2, display: "block", width: "100%" }}>
      <Paper
        elevation={0}
        component={motion.div}
        whileHover={{
          y: -2,
          boxShadow: theme.shadows[2],
          backgroundColor: alpha(theme.palette.background.paper, 0.9),
        }}
        sx={{
          mb: 2,
          borderRadius: 2,
          backgroundColor: getPaperBackgroundColor(),
          overflow: "hidden",
          border: `1px solid ${getBorderColor()}`,
          "&:hover": {
            cursor: "pointer",
            borderColor: getHoverBorderColor(),
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{ flex: 1 }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: getIconBackgroundColor(),
                  color: getIconColor(),
                  borderRadius: 1.5,
                  p: 0.75,
                  minWidth: 36,
                  height: 36,
                }}
              >
                {renderCustomerIcon()}
              </Box>

              <Stack spacing={0.3} sx={{ flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography
                    variant="body1"
                    fontWeight={500}
                    sx={{
                      color: getNameColor(),
                      textDecoration: getNameTextDecoration(),
                    }}
                  >
                    {customer.name}
                  </Typography>

                  {renderChannelAvatar()}
                  {renderBlacklistedChip()}
                </Stack>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={{ xs: 0.5, sm: 2 }}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                >
                  {renderPhoneNumber()}
                  {renderEmail()}
                  {renderAddress()}
                </Stack>
              </Stack>
            </Stack>

            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mt: { xs: 1, sm: 0 } }}
            >
              <Stack direction="row" spacing={0.5}>
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Tooltip title="Editar Cliente">
                    <IconButton
                      size="small"
                      onClick={editAction}
                      sx={{
                        color: theme.palette.primary.main,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        p: 0.75,
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.2),
                        },
                      }}
                    >
                      <EditIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Tooltip title="Excluir Cliente">
                    <IconButton
                      size="small"
                      onClick={deleteAction}
                      sx={{
                        color: theme.palette.error.main,
                        bgcolor: alpha(theme.palette.error.main, 0.1),
                        p: 0.75,
                        "&:hover": {
                          bgcolor: alpha(theme.palette.error.main, 0.2),
                        },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </motion.div>
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
}
